
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LegalDocumentDescription} from './LegalDocumentDescription';
import {FieldModelLegalDocumentDescription} from './LegalDocumentDescription';

/**
 * LegalDocumentGetResult struct
 */
class LegalDocumentGetResult {
  /**
   * Initialize struct
   * @param {!LegalDocumentDescription=} description
   * @param {!Uint8Array=} documentContent
   * @constructor
   */
  constructor (description = new LegalDocumentDescription(), documentContent = new Uint8Array(0)) {
    this.Description = description
    this.DocumentContent = documentContent
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentGetResult}
   * @param {!LegalDocumentGetResult} other Other struct
   * @returns {!LegalDocumentGetResult} This struct
   */
  copy (other) {
    if (other.Description != null) {
      this.Description = LegalDocumentDescription.fromObject(other.Description)
    } else {
      this.Description = null
    }
    if (other.DocumentContent != null) {
      if (typeof other.DocumentContent === 'string') {
        // noinspection JSUnresolvedFunction
        this.DocumentContent = Uint8Array.from(Buffer.from(other.DocumentContent, 'base64'))
      } else {
        this.DocumentContent = Uint8Array.from(other.DocumentContent)
      }
    } else {
      this.DocumentContent = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentGetResult}
   * @returns {!LegalDocumentGetResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentGetResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentGetResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentGetResult}
   * @param {!LegalDocumentGetResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentGetResult)) {
      throw new TypeError('Instance of LegalDocumentGetResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentGetResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Description: ((this.Description != null) ? this.Description : null),
      DocumentContent: ((this.DocumentContent != null) ? Buffer.from(this.DocumentContent).toString('base64') : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentGetResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentGetResult} other Object value
   * @returns {!LegalDocumentGetResult} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentGetResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentGetResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentGetResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentGetResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 15027
  }
}

export { LegalDocumentGetResult };

/**
 * Fast Binary Encoding LegalDocumentGetResult field model
 */
class FieldModelLegalDocumentGetResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Description = new FieldModelLegalDocumentDescription(buffer, 4 + 4)
    this.DocumentContent = new fbe.FieldModelBytes(buffer, this.Description.fbeOffset + this.Description.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Description.fbeSize + this.DocumentContent.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Description.fbeExtra + this.DocumentContent.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentGetResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 15027
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Description.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Description.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.DocumentContent.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentContent.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentContent.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!LegalDocumentGetResult} fbeValue Default value, defaults is new LegalDocumentGetResult()
   * @returns {!LegalDocumentGetResult} LegalDocumentGetResult value
   */
  get (fbeValue = new LegalDocumentGetResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!LegalDocumentGetResult} fbeValue LegalDocumentGetResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Description.fbeSize) <= fbeStructSize) {
      fbeValue.Description = this.Description.get()
    } else {
      fbeValue.Description = new LegalDocumentDescription()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Description.fbeSize

    if ((fbeCurrentSize + this.DocumentContent.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentContent = this.DocumentContent.get()
    } else {
      fbeValue.DocumentContent = new Uint8Array(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentContent.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {!LegalDocumentGetResult} fbeValue LegalDocumentGetResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentGetResult}
   * @param {LegalDocumentGetResult} fbeValue LegalDocumentGetResult value
   */
  setFields (fbeValue) {
    this.Description.set(fbeValue.Description)
    this.DocumentContent.set(fbeValue.DocumentContent)
  }
}

export { FieldModelLegalDocumentGetResult };

/**
 * Fast Binary Encoding LegalDocumentGetResult model
 */
class LegalDocumentGetResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentGetResult(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentGetResult model
   * @this {!LegalDocumentGetResultModel}
   * @returns {!FieldModelLegalDocumentGetResult} model LegalDocumentGetResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentGetResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentGetResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentGetResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentGetResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentGetResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentGetResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentGetResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentGetResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentGetResultModel}
   * @param {!LegalDocumentGetResult} value LegalDocumentGetResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentGetResultModel}
   * @param {!LegalDocumentGetResult} value LegalDocumentGetResult value, defaults is new LegalDocumentGetResult()
   * @return {!object} Deserialized LegalDocumentGetResult value and its size
   */
  deserialize (value = new LegalDocumentGetResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentGetResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentGetResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentGetResultModel}
   * @param {!number} prev Previous LegalDocumentGetResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentGetResultModel };
